// Dependency-free so both `server/` (reply prompt) and `src/` (inbox UI) can import it.
//
// `engagement_items.shopify_order_data` stores `lookupOrderInfo`'s result verbatim
// (fetch-engagement only writes it when `found`):
//   { found, order: { name, order_number, status, total: 'USD 89.50', items: [{name,
//     quantity, price}], tracking_number, tracking_url, ... }, platform }
// A flat `{ name, fulfillment_status, line_items, ... }` object (dev seed data) is
// also accepted. Every consumer must go through one of the normalisers below.

/** The flat order fields the reply prompt reads. */
export interface PromptOrder {
  name: string;
  fulfillment_status: string | null;
  tracking_number: string | null;
  tracking_url: string | null;
}

/** The flat order the inbox UI reads; structurally the client's `ShopifyOrderData`. */
export interface ClientOrder {
  name: string;
  status: string;
  fulfillment_status: string;
  tracking_number?: string;
  tracking_url?: string;
  tracking_company?: string;
  total_price: string;
  currency: string;
  created_at: string;
  line_items: Array<{ title: string; quantity: number }>;
}

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v.trim() : null;

/** The order record inside either shape, or null when there is no usable one. */
function orderSource(raw: unknown): { src: Record<string, unknown>; name: string } | null {
  if (!raw || typeof raw !== 'object') return null;
  const top = raw as Record<string, unknown>;
  if (top.found === false) return null;
  const src =
    top.order && typeof top.order === 'object' ? (top.order as Record<string, unknown>) : top;
  const name = str(src.name) ?? str(src.order_number);
  if (!name) return null;
  return { src, name: name.replace(/^#/, '') };
}

/**
 * Normalise `shopify_order_data` for the reply prompt. Returns null when there is
 * no usable order (found:false, missing/blank name), so the caller passes no order
 * and claims no order grounding.
 */
export function normalizeOrderData(raw: unknown): PromptOrder | null {
  const found = orderSource(raw);
  if (!found) return null;
  const { src, name } = found;
  return {
    name,
    fulfillment_status: str(src.fulfillment_status) ?? str(src.status),
    tracking_number: str(src.tracking_number),
    tracking_url: str(src.tracking_url),
  };
}

function lineItems(src: Record<string, unknown>): Array<{ title: string; quantity: number }> {
  const list = Array.isArray(src.line_items)
    ? src.line_items
    : Array.isArray(src.items)
      ? src.items
      : [];
  const out: Array<{ title: string; quantity: number }> = [];
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    const title = str(e.title) ?? str(e.name);
    if (!title) continue;
    out.push({ title, quantity: typeof e.quantity === 'number' ? e.quantity : 1 });
  }
  return out;
}

/** Split a stored `total` like "USD 89.50" into currency + amount. */
function splitTotal(src: Record<string, unknown>): { total_price: string; currency: string } {
  const price = str(src.total_price);
  const currency = str(src.currency);
  if (price) return { total_price: price, currency: currency ?? '' };
  const total = str(src.total);
  if (!total) return { total_price: '', currency: currency ?? '' };
  const m = total.match(/^([A-Za-z]{3})\s+(.+)$/);
  return m
    ? { total_price: m[2], currency: m[1].toUpperCase() }
    : { total_price: total, currency: '' };
}

/**
 * Normalise `shopify_order_data` into the flat shape the inbox UI reads. Always
 * returns a `line_items` array. Returns null when there is no usable order.
 */
export function normalizeClientOrderData(raw: unknown): ClientOrder | null {
  const found = orderSource(raw);
  if (!found) return null;
  const { src, name } = found;
  const status = str(src.status) ?? str(src.fulfillment_status) ?? '';
  const out: ClientOrder = {
    name,
    status,
    fulfillment_status: str(src.fulfillment_status) ?? status,
    ...splitTotal(src),
    created_at: str(src.created_at) ?? '',
    line_items: lineItems(src),
  };
  const trackingNumber = str(src.tracking_number);
  const trackingUrl = str(src.tracking_url);
  const trackingCompany = str(src.tracking_company);
  if (trackingNumber) out.tracking_number = trackingNumber;
  if (trackingUrl) out.tracking_url = trackingUrl;
  if (trackingCompany) out.tracking_company = trackingCompany;
  return out;
}
